import React from 'react'
import { AgGridWrapper, gridOptionsBase, TypedValueGetterParams, StatusCellRenderer } from 'view/components/datagrid'
import { ColGroupDef, ColDef, GridOptions } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { CollateralBasketProductsPanelGridRow, baseRowStyle } from './CollateralBasketDialogUtils'
import { Market } from 'api'
import { getCollateralStatusColor } from 'view/shared/datagrid'
import { DeleteButtonRenderer } from '../../components/datagrid/renderers/DeleteButtonRenderer'

interface CollateralBasketSelectedProductsGridProps {
  rowData: CollateralBasketProductsPanelGridRow[]
  canDelete: boolean
  removeCollateralId?: (collateralIds: string[]) => void
  collateralBasketMarket?: Market
}

const getProductsGridColumnDefs: (canDelete: boolean, collateralBasketMarket?: Market) => (ColDef | ColGroupDef)[] = (
  canDelete,
  collateralBasketMarket
) => [
  {
    colId: 'PRODUCT_NAME',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    minWidth: 80,
    resizable: true,
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: CollateralBasketProductsPanelGridRow) => data.collateral && getCollateralStatusColor(data.collateral.status),
      isBorderRadius: true
    }
  },
  {
    colId: 'PRODUCT_SECTOR_NAME',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    minWidth: 100,
    resizable: true
  },
  {
    colId: 'COLLATERAL_NAME',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.collateral?.name,
    headerName: Locale.translate(Translation.TC_COLLATERAL),
    minWidth: 150,
    resizable: true
  },
  {
    colId: 'COLLATERAL_ID',
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) =>
      collateralBasketMarket === Market.US ? params.data?.collateral?.cusip : params.data?.collateral?.isin,
    headerName: Locale.translate(collateralBasketMarket === Market.US ? Translation.TC_CUSIP : Translation.TC_ISIN),
    minWidth: 150,
    resizable: true
  },
  ...(canDelete
    ? [
        {
          colId: 'DELETE_COLLATERAL',
          minWidth: 40,
          minminWidth: 40,
          cellRenderer: DeleteButtonRenderer,
          cellStyle: {
            borderLeft: '1px solid #e1e3e7'
          },
          resizable: true
        }
      ]
    : [])
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.rowId,
  rowStyle: baseRowStyle
}

export const CollateralBasketSelectedProductsGrid: React.FunctionComponent<CollateralBasketSelectedProductsGridProps> = props => {
  const { rowData, canDelete, removeCollateralId, collateralBasketMarket } = props

  const handleDelete = (_rowId: string, rowItem: CollateralBasketProductsPanelGridRow, _updatedField: string) => {
    const collateralId = rowItem.collateral?.id
    if (removeCollateralId && collateralId) {
      removeCollateralId([collateralId])
    }
  }

  return (
    <AgGridWrapper
      className="datagrid-borderless"
      gridOptions={gridOptions}
      columnDefs={getProductsGridColumnDefs(canDelete, collateralBasketMarket)}
      rowData={rowData}
      onCellValueUpdated={handleDelete}
    />
  )
}
